/**
 * The pieces every Data panel is drawn from: the card, a stat tile, bars, a
 * meter, and the formatters that turn a missing value into words.
 *
 * Same tokens as Supply and the Health register, so the Data page reads as
 * part of the same app. Magnitude is drawn in one neutral hue; the status
 * colours mean status and nothing else, because a chart that colours ten
 * aircraft ten ways puts most of them below the difference anyone can see.
 */

import type { ReactNode } from 'react';

export const OK = 'oklch(0.78 0.15 155)';
export const WARN = 'oklch(0.80 0.15 85)';
export const DANGER = 'oklch(0.72 0.19 28)';
export const WHITE = '#f4f4f5';
export const MUTED = 'rgba(244,244,245,0.5)';
export const FAINT = 'rgba(244,244,245,0.42)';
export const BAR = 'rgba(244,244,245,0.62)';
export const CARD = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

/** The one place a missing number becomes text. Never 0. */
export const NO_DATA = 'n/a';

export function num(value: number | null | undefined, digits = 0): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? NO_DATA
    : value.toFixed(digits);
}

export function pct(fraction: number | null | undefined, digits = 0): string {
  return fraction === null || fraction === undefined ? NO_DATA : `${(fraction * 100).toFixed(digits)}%`;
}

export function hours(value: number | null | undefined): string {
  return value === null || value === undefined ? NO_DATA : `${value.toFixed(1)} h`;
}

export function date(seconds: number | null | undefined): string {
  if (!seconds) return NO_DATA;
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function dateTime(seconds: number | null | undefined): string {
  if (!seconds) return NO_DATA;
  return new Date(seconds * 1000).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Panel({
  title,
  subtitle,
  right,
  children,
  tone,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
  children: ReactNode;
  tone?: string;
}) {
  return (
    <section
      className="rounded-[18px] border"
      style={{
        background: CARD,
        borderColor: tone ? `color-mix(in oklch, ${tone} 40%, transparent)` : 'rgba(255,255,255,0.13)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
        padding: '16px 18px',
      }}
      aria-label={title}
    >
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <h2 className="label-mono text-[9.5px]" style={{ color: MUTED, letterSpacing: '0.16em' }}>
          {title}
        </h2>
        {right}
      </div>
      {subtitle ? (
        <p className="mb-3 text-[10.5px]" style={{ color: FAINT }}>
          {subtitle}
        </p>
      ) : (
        <div className="mb-3" />
      )}
      {children}
    </section>
  );
}

export function StatTile({
  label,
  value,
  note,
  tone = WHITE,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: string;
}) {
  return (
    <div className="flex min-w-[110px] flex-col">
      <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
        {label}
      </span>
      <span className="tnum text-[30px] font-semibold leading-none" style={{ color: tone }}>
        {value}
      </span>
      {note ? (
        <span className="label-mono mt-1 text-[8.5px]" style={{ color: MUTED }}>
          {note}
        </span>
      ) : null}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px]" style={{ color: MUTED }}>
      {children}
    </p>
  );
}

export interface BarDatum {
  label: string;
  value: number;
  /** Shown at the end of the bar. Defaults to the value. */
  display?: string;
  tone?: string;
  title?: string;
}

/** Horizontal bars on one scale, labelled in the row rather than by colour. */
export function Bars({ data, empty }: { data: BarDatum[]; empty: string }) {
  if (data.length === 0) return <Empty>{empty}</Empty>;
  const max = Math.max(...data.map((d) => d.value), 0);
  return (
    <div className="flex flex-col gap-1.5">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-2 text-[11px]" title={d.title}>
          <span className="w-[38%] truncate" style={{ color: WHITE }}>
            {d.label}
          </span>
          <div className="relative h-[8px] flex-1 rounded-[4px] bg-black/25">
            <div
              className="absolute inset-y-0 left-0 rounded-[4px]"
              style={{
                width: `${max > 0 && d.value > 0 ? Math.max(2, (d.value / max) * 100) : 0}%`,
                background: d.tone ?? BAR,
              }}
            />
          </div>
          <span className="tnum w-[64px] text-right" style={{ color: MUTED }}>
            {d.display ?? String(d.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * One lane of daily columns. Small multiples, each on its own scale, so a
 * series with ten times the counts does not flatten a rare and serious one to
 * nothing on a shared axis.
 */
export function DayColumns({
  label,
  values,
  tone = BAR,
  total,
}: {
  label: string;
  values: number[];
  tone?: string;
  total: string;
}) {
  const max = Math.max(...values, 0);
  const width = 600;
  const step = values.length > 0 ? width / values.length : width;
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-[74px] flex-none label-mono text-[8px]" style={{ color: MUTED }}>
        {label}
      </span>
      <svg
        viewBox={`0 0 ${width} 30`}
        preserveAspectRatio="none"
        className="block h-[30px] w-full min-w-0 flex-1 rounded-[6px] bg-black/20"
        role="img"
        aria-label={`${label} per day`}
      >
        {max > 0
          ? values.map((v, i) =>
              v > 0 ? (
                <rect
                  key={i}
                  x={i * step + step * 0.15}
                  width={step * 0.7}
                  y={30 - (v / max) * 28}
                  height={(v / max) * 28}
                  fill={tone}
                />
              ) : null,
            )
          : null}
      </svg>
      <span className="tnum w-[56px] flex-none text-right text-[10px]" style={{ color: MUTED }}>
        {total}
      </span>
    </div>
  );
}

/** A ratio against a limit, with the limit drawn. */
export function Meter({ fraction, tone }: { fraction: number; tone: string }) {
  return (
    <div className="relative h-[6px] w-full rounded-[3px] bg-black/25">
      <div
        className="absolute inset-y-0 left-0 rounded-[3px]"
        style={{ width: `${Math.min(1, Math.max(0, fraction)) * 100}%`, background: tone }}
      />
    </div>
  );
}

export function Chip({ children, tone }: { children: ReactNode; tone: string }) {
  return (
    <span
      className="rounded-[99px] px-1.5 py-px label-mono text-[8px] whitespace-nowrap"
      style={{ color: tone, background: `color-mix(in oklch, ${tone} 16%, transparent)` }}
    >
      {children}
    </span>
  );
}

export function Table({
  head,
  rows,
  empty,
}: {
  head: string[];
  rows: ReactNode[][];
  empty: string;
}) {
  if (rows.length === 0) return <Empty>{empty}</Empty>;
  return (
    <div className="scroll-thin overflow-x-auto">
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={h}
                className={`label-mono pb-1.5 text-[8px] font-normal ${i === 0 ? 'text-left' : 'pl-3 text-right'}`}
                style={{ color: MUTED }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="border-t border-white/5">
              {row.map((cell, c) => (
                <td
                  key={c}
                  className={`tnum py-1 ${c === 0 ? 'text-left' : 'pl-3 text-right'}`}
                  style={{ color: c === 0 ? WHITE : MUTED }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  placeholder,
  mono,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  type?: 'text' | 'number';
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="label-mono text-[8px]" style={{ color: MUTED }}>
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`well h-8 rounded-[9px] px-2.5 text-[12px] outline-none ${mono ? 'font-mono' : ''}`}
      />
    </label>
  );
}

export function PillButton({
  children,
  onClick,
  active,
  disabled,
  tone,
}: {
  children: ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  tone?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className="rounded-[99px] border px-3 py-[5px] label-mono text-[9px] transition-colors disabled:opacity-40"
      style={{
        color: tone ?? (active ? '#0c0c0d' : WHITE),
        background: active ? 'oklch(1 0 0 / 0.92)' : 'oklch(1 0 0 / 0.05)',
        borderColor: tone ? `color-mix(in oklch, ${tone} 45%, transparent)` : 'rgba(255,255,255,0.13)',
      }}
    >
      {children}
    </button>
  );
}
